// Subtasks — a task split into smaller tasks (docs/plans/2026-10-03-subtaken.md).
//
// A subtask is an ordinary task with `parentTaskId`. It starts as a draft
// (status `pending`, no workflow state); the parent's `kind: subtasks`
// workflow step starts drafts, at most `maxParallel` at once, once every
// task that blocks them (`task_links` kind `blocks`) has completed.

import type { TaskStatus } from './task.js'

/** Most subtasks one parent task can have. */
export const MAX_SUBTASKS_PER_TASK = 50
/** Most subtasks one `create_subtasks` call can create or update. */
export const MAX_SUBTASKS_PER_CALL = 20
/** Format of a subtask key. */
export const SUBTASK_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,39}$/

/**
 * Where a subtask stands, as the parent sees it:
 * - `draft`: not started (status `pending`, no workflow state);
 * - `running`: started and not finished (also when it is blocked — `blocked` says so);
 * - `succeeded`: status `completed`;
 * - `failed`: finished without success (cancelled, rejected, or a terminal state that isn't success).
 */
export type SubtaskPhase = 'draft' | 'running' | 'succeeded' | 'failed'

export interface SubtaskSummary {
  total: number
  drafts: number
  running: number
  /** Of `running`: how many are blocked and wait for a person. */
  blocked: number
  succeeded: number
  failed: number
}

/** One row of GET /tasks/:id/subtasks. */
export interface SubtaskView {
  id: string
  key: string
  order: number
  title: string
  status: TaskStatus
  phase: SubtaskPhase
  workflowKey: string | null
  workflowState: string | null
  pullRequestUrl: string | null
  /** Keys of the sibling subtasks that block this one (incoming `blocks` links). */
  blockedBy: string[]
  costCents: number
  createdAt: string
  updatedAt: string
}

/** GET /tasks/:id/subtasks. */
export interface SubtasksResponse {
  parentTaskId: string
  summary: SubtaskSummary
  subtasks: SubtaskView[]
}

/** One subtask to create or update (draft). Same shape for the `create_subtasks` run-tool, POST /tasks/:id/subtasks and MCP. */
export interface SubtaskDraftInput {
  key: string
  title: string
  instructions: string
  acceptanceCriteria?: string[]
  /** Workflow key; null/absent = the parent step's `childWorkflow`, else the project default. */
  workflow?: string | null
  /** Keys of sibling subtasks (new or existing) this one waits for. */
  dependsOn?: string[]
}

/** Result of creating/updating a batch of drafts. */
export interface SubtaskBatchResult {
  created: { key: string; taskId: string }[]
  updated: { key: string; taskId: string }[]
  skipped: { key: string; reason: string }[]
  /** Drafts removed by `replace: true`. */
  removed: string[]
}

/** The "Part of a larger task" context a subtask's run gets (prompt + `get_task`). */
export interface SubtaskRunContext {
  parent: { id: string; title: string; instructions: string }
  key: string
  siblings: { key: string; title: string; phase: SubtaskPhase }[]
  /** The subtasks this one waited for, with their last step's summary. */
  dependencies: { key: string; title: string; summary: string | null }[]
}
